<template>
  <v-dialog v-model="showDialog" min-width="500px" max-width="750px" persistent>
    <v-card class="dialog-padding">
      <v-card-title class="d-flex justify-space-between align-center pa-4">
        <span class="dialog-title">
          {{ $t('__selectCustomer') }}
        </span>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="px-4 pb-0 keyword-bar">
        <!-- Keyword Search -->
        <v-text-field
          outlined
          :placeholder="$t('__keywordSearch')"
          class="mt-1 mb-2 keyword text_field"
          dense
          v-model="searchKeyword"
          background-color="white"
          hide-details
          @keyup.enter="handleSearch"
        >
          <template v-slot:append>
            <v-icon color="white" style="background:#ED6D00; cursor: pointer;" @click="handleSearch">
              icon-form_search
            </v-icon>
          </template>
        </v-text-field>

        <!-- Customer List Table -->
        <div id="listTable" style="margin:0; padding:0; max-height: 400px; overflow-y: auto;">
          <v-simple-table fixed-header>
            <template v-slot:default>
              <thead>
                <tr>
                  <th style="width: 40px;" class="text-center"></th>
                  <th class="text-left" style="min-width: 130px;">{{ $t('__customerName') }}</th>
                  <th class="text-left" style="min-width: 100px;">{{ $t('__abbreviation') }}</th>
                  <th class="text-left" style="min-width: 110px;">{{ $t('__contactPerson') }}</th>
                  <th class="text-left" style="min-width: 110px;">{{ $t('__tel') }}</th>
                  <th class="text-left" style="min-width: 140px;">{{ $t('__address') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in filteredCustomerList"
                  :key="item.id || item.name"
                  @click="toggleCustomer(item)"
                  style="cursor: pointer;"
                  :class="{ 'emp-selected-row': isCustomerSelected(item) }"
                >
                  <td class="text-center">
                    <v-icon v-if="isCustomerSelected(item)" color="#ED6D00" size="20">mdi-checkbox-marked</v-icon>
                    <v-icon v-else color="#999" size="20">mdi-checkbox-blank-outline</v-icon>
                  </td>
                  <td class="text-left">{{ item.name }}</td>
                  <td class="text-left">{{ item.abbreviation }}</td>
                  <td class="text-left">{{ item.contactPerson }}</td>
                  <td class="text-left">{{ item.phoneNumber }}</td>
                  <td class="text-left">{{ item.address }}</td>
                </tr>
                <tr v-if="filteredCustomerList.length === 0">
                  <td colspan="6" class="text-center grey--text py-4">
                    <span v-if="isLoading">{{ $t('__loading') || 'Loading...' }}</span>
                    <span v-else>{{ $t('__noCustomersFound') }}</span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>

        <!-- Selected summary -->
        <div class="mt-3 text-body-2 d-flex flex-wrap align-center" style="color: #666; gap: 8px;">
          <span>{{ $t('__selected') }}:</span>
          <template v-if="selectedCustomer">
            <v-chip
              class="ma-1"
              small
              close
              outlined
              color="#ED6D00"
              @click:close="selectedCustomer = null"
            >
              {{ selectedCustomer.name }}{{ selectedCustomer.abbreviation ? ` (${selectedCustomer.abbreviation})` : '' }}
            </v-chip>
          </template>
          <span v-else><strong>{{ $t('__none') }}</strong></span>
        </div>
      </v-card-text>

      <v-card-actions class="justify-center pb-4 px-4 mt-2">
        <v-btn class="colorBtn" outlined color="#FFF" @click="close">{{ $t('__cancel') }}</v-btn>
        <v-btn class="colorBtn primary-color-background" outlined color="#FFF" @click="confirmSelection">{{ $t('__confirm') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'CustomerSelectDialog',
  data() {
    return {
      showDialog: false,
      isLoading: false,
      searchKeyword: '',
      customerList: [],
      selectedCustomer: null
    }
  },
  computed: {
    filteredCustomerList() {
      if (!this.searchKeyword) return this.customerList
      const kw = this.searchKeyword.toLowerCase().trim()
      return this.customerList.filter(item => {
        return (
          (item.name && item.name.toLowerCase().includes(kw)) ||
          (item.abbreviation && item.abbreviation.toLowerCase().includes(kw)) ||
          (item.contactPerson && item.contactPerson.toLowerCase().includes(kw)) ||
          (item.phoneNumber && item.phoneNumber.toLowerCase().includes(kw)) ||
          (item.address && item.address.toLowerCase().includes(kw)) ||
          (item.emailAddress && item.emailAddress.toLowerCase().includes(kw)) ||
          (item.id && String(item.id).toLowerCase().includes(kw))
        )
      })
    }
  },
  methods: {
    open(currentValue) {
      this.searchKeyword = ''
      this.showDialog = true
      
      // Load customers if not yet loaded
      if (this.customerList.length === 0) {
        this.loadCustomers(currentValue)
      } else {
        this.matchCurrentValue(currentValue)
      }
    },
    loadCustomers(currentValue) {
      this.isLoading = true
      const params = {
        pageSize: 1000,
        currentPage: 1,
        orderBy: 'id',
        direction: 'asc'
      }
      this.$http.get('/GetCustomerList', { params })
        .then(response => {
          let data = []
          if (response.data.Data) {
            data = response.data.Data
          } else if (response.data.data) {
            data = response.data.data
          } else if (Array.isArray(response.data)) {
            data = response.data
          }
          this.customerList = data
          if (currentValue !== undefined) {
            this.matchCurrentValue(currentValue)
          }
        })
        .catch(err => {
          console.error('Failed to load customers:', err)
          this.customerList = []
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    handleSearch() {
      // Backend search if needed to refresh full list
      if (!this.searchKeyword.trim()) {
        this.loadCustomers(this.selectedCustomer ? this.selectedCustomer.name : null)
        return
      }
      this.isLoading = true
      const params = {
        keyword: this.searchKeyword.trim(),
        pageSize: 1000,
        currentPage: 1
      }
      this.$http.get('/GetCustomerList', { params })
        .then(response => {
          let data = []
          if (response.data.Data) {
            data = response.data.Data
          } else if (response.data.data) {
            data = response.data.data
          } else if (Array.isArray(response.data)) {
            data = response.data
          }
          this.customerList = data
        })
        .catch(err => {
          console.error('Customer search error:', err)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    matchCurrentValue(currentValue) {
      if (!currentValue) {
        this.selectedCustomer = null
        return
      }
      const valStr = typeof currentValue === 'object' ? currentValue.name : currentValue
      const found = this.customerList.find(c => c.name === valStr)
      if (found) {
        this.selectedCustomer = found
      } else if (typeof currentValue === 'object' && currentValue.name) {
        this.selectedCustomer = currentValue
      } else {
        this.selectedCustomer = { name: valStr }
      }
    },
    isCustomerSelected(item) {
      if (!this.selectedCustomer || !item) return false
      if (this.selectedCustomer.id && item.id) {
        return this.selectedCustomer.id === item.id
      }
      return this.selectedCustomer.name === item.name
    },
    toggleCustomer(item) {
      if (this.isCustomerSelected(item)) {
        this.selectedCustomer = null
      } else {
        this.selectedCustomer = item
      }
    },
    confirmSelection() {
      this.$emit('confirm', this.selectedCustomer)
      this.$emit('input', this.selectedCustomer ? this.selectedCustomer.name : '')
      this.showDialog = false
    },
    close() {
      this.showDialog = false
    }
  }
}
</script>

<style scoped>
.emp-selected-row {
  background-color: #FFF3E0 !important;
}
.emp-selected-row:hover {
  background-color: #FFE0B2 !important;
}
</style>
